import React, { ChangeEvent, useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import {
  getAutoActionsConfig,
  updateAutoActionsConfig,
  deleteAutoActionsConfig,
  getAllNotifications
} from 'services/firebase';
import {
  Button,
  Typography,
  IconButton,
  Select,
  Input,
  MenuItem,
  DialogContent,
  Tabs,
  Tab,
  SelectChangeEvent
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { days } from 'utils/parsingUtils';
import { useSelector } from 'react-redux';
import { getCellName } from 'selectors/ordersSelector';
import { convertUtcTimeKeyToLocal, convertLocalTimeKeyToUtc } from '../../../utils/autoActionTimeKey';
import { Notification } from '../../../actions/uiActions';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 500
    }
  }
};

const AutoActionsLoading = () => {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell component="th" scope="row">
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} style={{ float: 'right' }} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} style={{ float: 'right' }} />
      </TableCell>
    </TableRow>
  );
};

const flagActions = [
  'STORES_ENABLE',
  'STORES_DISABLE',
  'PREORDER_ENABLE',
  'PREORDER_DISABLE',
  'AUTO_ACCEPT_ENABLE',
  'ASAP_ENABLE',
  'ASAP_DISABLE'
];

const notificationActions = ['NOTIFICATION_ENABLE', 'NOTIFICATION_DISABLE'];

interface AutoActionsRowProps {
  actions: any; // This is really `string[] | {type: string, data: any}[];`...which Typescript doesn't like. We should refactor.
  timeKey: string;
  day: string;
  notify: (data: Notification) => void;
  refreshAutoActions: (day: string) => void;
  availableNotifs: {
    id: string,
    type: string,
    html: string,
    active?: boolean
  }[];
  cellName: string;
}

const AutoActionsRow = ({
  actions,
  timeKey,
  day,
  notify,
  refreshAutoActions,
  availableNotifs,
  cellName
}: AutoActionsRowProps) => {
  const isNotificationRow: boolean = !!actions.find(item => typeof item !== 'string');
  const initialSelectedActions = isNotificationRow ? actions[0].type : actions;

  const initialSelectedNotificationID = isNotificationRow
    ? actions[0].data.id
    : null;

  const [selected, setSelectedActions] = React.useState(initialSelectedActions);
  const [selectedNotificationID, setSelectedNotificationID] = React.useState<String | null>(
    initialSelectedNotificationID
  );
  const [displayTime, setDisplayTimeKey] = React.useState(convertUtcTimeKeyToLocal(timeKey));
  const initialTime = React.useRef(timeKey);

  const handleChangeActions = (evt: SelectChangeEvent<string>) => {
    setSelectedActions(evt.target.value);
  };

  const handleNotificationChange = (evt: SelectChangeEvent<string>) => {
    setSelectedNotificationID(evt.target.value);
  };

  const handleChangeDisplayTimeKey = (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDisplayTimeKey(evt.target.value);
  };

  const onSave = () => {
    if (isNotificationRow) {
      const selectedNotifWithId = availableNotifs.find(
        notif => notif.id === selectedNotificationID
      );
      updateAutoActionsConfig(
        {
          cellName,
          day,
          time: convertLocalTimeKeyToUtc(displayTime),
          previousTime: initialTime.current,
          actions: [{ type: selected, data: selectedNotifWithId }]
        },
        onUpdateSuccess,
        onUpdateFailure
      );
    } else {
      updateAutoActionsConfig(
        {
          cellName,
          day,
          time: convertLocalTimeKeyToUtc(displayTime),
          previousTime: initialTime.current,
          actions: selected
        },
        onUpdateSuccess,
        onUpdateFailure
      );
    }
  };

  const onUpdateSuccess = _res => {
    notify({ variant: 'success', message: 'Updated actions successfully' });
    refreshAutoActions(day);
  };

  const onUpdateFailure = err => {
    notify({ variant: 'error', message: 'Failed to update actions' });
    refreshAutoActions(day);
  };

  const onDelete = () => {
    deleteAutoActionsConfig(
      { cellName, day, time: convertLocalTimeKeyToUtc(displayTime) },
      () => {
        notify({ variant: 'success', message: 'Removed actions' });
        refreshAutoActions(day);
      },
      err => {
        notify({ variant: 'error', message: 'Failed to remove actions' });
        refreshAutoActions(day);
      }
    );
  };

  return (
    <TableRow>
      <TableCell>
        <TextField value={displayTime} onChange={handleChangeDisplayTimeKey} />
      </TableCell>
      <TableCell>
        <Select
          id="notification-action-select"
          className="max-w-xs"
          value={selected}
          multiple
          onChange={handleChangeActions}
          input={<Input />}
          MenuProps={MenuProps}>
          {isNotificationRow &&
            notificationActions.map(action => (
              <MenuItem key={action} value={action}>
                {action}
              </MenuItem>
            ))}
          {!isNotificationRow &&
            flagActions.map(action => (
              <MenuItem key={action} value={action}>
                {action}
              </MenuItem>
            ))}
        </Select>
      </TableCell>
      <TableCell>
        {isNotificationRow ? (
          <Select
            id="notifications-select"
            className="max-w-xs"
            value={selectedNotificationID}
            onChange={handleNotificationChange}
            input={<Input />}
            MenuProps={MenuProps}>
            {availableNotifs.map(notif => (
              <MenuItem
                key={notif.id}
                value={notif.id}
                className="border-b border-gray-200">
                <div dangerouslySetInnerHTML={{ __html: notif.html }} />
              </MenuItem>
            ))}
          </Select>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell align="right">
        <Button color="primary" onClick={onSave}>
          SAVE
        </Button>
      </TableCell>
      <TableCell align="right">
        <IconButton onClick={onDelete}>
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const NotificationOption = ({ notif, selected, onSelect }) => {
  return (
    <div
      onClick={() => onSelect(notif)}
      dangerouslySetInnerHTML={{ __html: notif.html }}
      style={{
        border: selected ? '1px solid blue' : 'none',
        width: '100%',
        fontFamily: 'Arial',
        boxShadow:
          '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
        borderRadius: 5,
        padding: 20,
        cursor: 'pointer',
        marginBottom: 10
      }}
    />
  );
};

const AddAutoActionDialog = ({
  onClose,
  open,
  day,
  notify,
  refreshAutoActions,
  availableNotifs,
  cellName
}) => {
  const [time, setTime] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedFlagActions, setSelectedFlagActions] = useState([]);
  const [selectedNotificationAction, setSelectedNotificationActions] = useState(
    []
  );
  const [selectedNotif, setSelectedNotif] = useState();

  const handleClose = () => {
    onClose();
  };

  const onClickAdd = () => {
    if (selectedTab === 0) {
      updateAutoActionsConfig(
        { cellName, day, time, actions: selectedFlagActions } as any,
        onAutoActionAddedSuccess,
        onAutoActionAddedFailure
      );
    } else {
      updateAutoActionsConfig(
        {
          cellName,
          day,
          time,
          actions: [{ type: selectedNotificationAction, data: selectedNotif }]
        } as any,
        onAutoActionAddedSuccess,
        onAutoActionAddedFailure
      );
    }
  };

  const onAutoActionAddedFailure = err => {
    notify({ variant: 'error', message: 'Failed to update actions' });
  };

  const onAutoActionAddedSuccess = () => {
    notify({ variant: 'success', message: 'Updated actions successfully' });
    refreshAutoActions(day);
    onClose();
  };

  const onSelectNotification = notif => {
    setSelectedNotif(notif);
  };

  const displayNotifcationOptions = selectedNotificationAction.includes(
    'NOTIFICATION_ENABLE'
  );

  const addActionBtnDisabled = (): boolean => {
    if (time === '') {
      return true;
    } else if (selectedFlagActions.length === 0 && selectedTab === 0) {
      return true;
    } else if (selectedNotificationAction.length === 0 && selectedTab === 1) {
      return true;
    } else if (displayNotifcationOptions && !selectedNotif) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}>
      <div className="w-96">
        <Tabs
          value={selectedTab}
          onChange={(_evt, val) => {
            setSelectedTab(val);
          }}
          variant="fullWidth"
          style={{ borderBottom: '1px solid #efefef' }}>
          <Tab label="Flags" />
          <Tab label="Notifications" />
        </Tabs>
        <DialogContent>
          <TextField
            fullWidth
            label="Time"
            onChange={e => setTime(e.target.value)}
            value={time}
          />
          {selectedTab === 0 && (
            <Select
              fullWidth
              multiple
              placeholder="Actions"
              className="mt-5 mb-5"
              value={selectedFlagActions}
              onChange={evt => setSelectedFlagActions(evt.target.value as any)}
              input={<Input />}
              MenuProps={MenuProps}>
              {flagActions.map(action => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </Select>
          )}
          {selectedTab === 1 && (
            <>
              <Select
                fullWidth
                placeholder="Actions"
                className="mt-5 mb-5"
                value={selectedNotificationAction}
                onChange={evt =>
                  setSelectedNotificationActions(evt.target.value as any)
                }
                input={<Input />}
                MenuProps={MenuProps}>
                {notificationActions.map(action => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </Select>
              <>
                {displayNotifcationOptions && availableNotifs.length && (
                  <>
                    <Typography variant="h5" className="mb-4">
                      Select Notification
                    </Typography>
                    <div className="h-72 overflow-auto p-1">
                      {availableNotifs.map(notif => {
                        return (
                          <NotificationOption
                            key={`notif-${notif.id}`}
                            selected={(selectedNotif as any)?.id === notif.id}
                            notif={notif}
                            onSelect={onSelectNotification}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            </>
          )}
          <Button
            disabled={addActionBtnDisabled()}
            fullWidth
            color="primary"
            variant="contained"
            className="my-2.5"
            onClick={onClickAdd}>
            Add Actions
          </Button>
        </DialogContent>
      </div>
    </Dialog>
  );
};

interface AutoActionsTableProps {
  notify: (data: Notification) => void
}
export default function AutoActionsTable(props: AutoActionsTableProps) {
  const [addAction, setAddAction] = useState<boolean>(false);
  const [autoActions, setAutoActions] = useState<{[key: number]: string[]}>([]);
  const [loadingAutoActions, setLoadingAutoActions] = useState<boolean>(false);
  const [availableNotifs, setAvailableNotifs] = useState([]);
  const [autoDay, setAutoDay] = useState<string>(days[new Date().getDay()]);

  const cellName: string = useSelector(getCellName);


  const fetchNotifications = () => {
    getAllNotifications(
      notifs => {
        setAvailableNotifs(notifs);
      },
      err => {
        props.notify({
          variant: 'error',
          message: 'Failed to fetch notifications'
        });
      }
    );
  };

  const fetchAutoActions = (autoDay: string) => {
    setLoadingAutoActions(true);
    getAutoActionsConfig(
      cellName,
      autoDay,
      (res: {[key: number]: string[]}) => {
        setAutoActions(res);
        setLoadingAutoActions(false);
      },
      (err) => {
        console.error({err})
        setLoadingAutoActions(false);
      }
    );
  };

  useEffect(() => {
    if (autoDay) {
      fetchAutoActions(autoDay);
      fetchNotifications();
    }
  }, [autoDay, cellName]);

  const handleChangeActionDay = evt => {
    const value = evt.target.value;
    setAutoDay(value);
  };

  return (
    <>
      <Paper className="relative">
        <Grid container className="p-4">
          <Grid item>
            <h3 className="font-sans text-lg">Auto Actions</h3>
          </Grid>
          <Grid item>
            <Button
              className="ml-5"
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setAddAction(true)}>
              Add Action
            </Button>
            <select
              className="ml-4 mt-1.5 px-2 py-1 border border-gray-300 rounded"
              value={autoDay}
              onChange={handleChangeActionDay}>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </select>
          </Grid>
        </Grid>
        <TableContainer className="h-96 pb-20">
          <Table className="min-w-2xl" size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Options</TableCell>
                <TableCell align="right"></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingAutoActions && (
                <>
                  <AutoActionsLoading />
                  <AutoActionsLoading />
                  <AutoActionsLoading />
                  <AutoActionsLoading />
                  <AutoActionsLoading />
                  <AutoActionsLoading />
                  <AutoActionsLoading />
                </>
              )}
              {!loadingAutoActions &&
                autoActions &&
                Object.keys(autoActions)
                  .sort((timeA, timeB) => {
                    return Number(timeA) - Number(timeB);
                  })
                  .map(key => {
                    return (
                      <AutoActionsRow
                        key={key}
                        actions={autoActions[key]}
                        availableNotifs={availableNotifs}
                        timeKey={key}
                        cellName={cellName}
                        day={autoDay}
                        notify={props.notify}
                        refreshAutoActions={day => fetchAutoActions(day)}
                      />
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {addAction && (
        <AddAutoActionDialog
          open={addAction}
          availableNotifs={availableNotifs}
          cellName={cellName}
          onClose={() => {
            setAddAction(false);
          }}
          refreshAutoActions={(day: string) => fetchAutoActions(day)}
          day={autoDay}
          notify={props.notify}
        />
      )}
    </>
  );
}
